import { useState, useEffect } from "react";
import { CLIENT_COLORS, CYCLE } from "./constants";
import { db } from "./db";
import { Spinner, Btn, Modal, Field, Inp, Sel, Txta, FormRow, Marca } from "./components/primitives";
import { PlataformasPicker } from "./components/PlataformasPicker";
import { parsePlataformas } from "./plataformas";
import { SkillsView } from "./views/SkillsView";
import { SocialMediaView } from "./views/SocialMediaView";
import { ProspectView } from "./views/ProspectView";
import { ClaudeDesignView } from "./views/ClaudeDesignView";
import { PerformanceView } from "./views/PerformanceView";
import { LoginView } from "./views/LoginView";
import { Sidebar } from "./components/Sidebar";
import { DashboardView } from "./views/DashboardView";
import { ClientsView } from "./views/ClientsView";
import { ClientDetail } from "./views/ClientDetail";
import { CriativosView, CriativoFormModal } from "./views/CriativosView";
import { ConcorrentesView } from "./views/ConcorrentesView";
import { RoteirosView } from "./views/RoteirosView";
import { HooksView } from "./views/HooksView";
import { PromptsView } from "./views/PromptsView";
import { AgendaView } from "./views/AgendaView";
import { BibliotecaView } from "./views/BibliotecaView";
import { CrmMedicoView } from "./views/CrmMedicoView";
import { RotinaDoDiaView } from "./views/RotinaDoDiaView";
import { AutomacoesView } from "./views/AutomacoesView";
import { FluxoMedView } from "./views/FluxoMedView";

function ClientFormModal({ initial, onClose, onSave }) {
  const [f, setF] = useState(initial || {
    name: "", type: "Médico", status: "Ativo", specialty: "",
    links: "", notes: "", cycle_stage: "1-onboarding", avatar_color: "#378ADD",
    publico_alvo: "", orcamento_total: "", orcamento_meta: "", orcamento_google: "",
    cidades: "", dia_relatorio: "", cliente_envia_criativos: false,
    site: "", whatsapp: "", whatsapp_secretaria: "", nome_secretaria: "", dores: "", objecoes: "", solucao: "", transformacao: "", estrategias_utilizadas: "",
    convenios_atende: "", convenios_nao_atende: "",
    funil_topo:  { objetivo: "", publico: "", criativos: "" },
    funil_meio:  { objetivo: "", publico: "", criativos: "" },
    funil_fundo: { objetivo: "", publico: "", criativos: "" },
  });
  const upFunil = (etapa, k, v) => setF((p) => ({ ...p, [etapa]: { ...(p[etapa] || {}), [k]: v } }));
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [saving, setSaving] = useState(false);
  async function save() {
    if (!f.name.trim()) return;
    setSaving(true);
    try {
      await onSave(f);
    } catch (e) {
      console.error("Erro ao salvar cliente:", e);
      alert("Erro ao salvar: " + (e.message || "Tente novamente."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={initial ? "Editar cliente" : "Novo cliente"} onClose={onClose} width={480}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label="Nome"><Inp value={f.name} onChange={(e) => up("name", e.target.value)} placeholder="Nome completo ou empresa..." /></Field>
        {/* Grupo fica fora do nome: o nome é o que os outros sistemas mostram (e o portal do cliente). */}
        <Field label="Empresa do grupo">
          <Inp value={f.grupo || ""} onChange={(e) => up("grupo", e.target.value)} placeholder="Opcional. Ex: Grupo THE" />
        </Field>
        <FormRow>
          <Field label="Tipo" half>
            <Sel value={f.type} onChange={(e) => up("type", e.target.value)}>
              {Object.keys(CLIENT_COLORS).map((t) => <option key={t}>{t}</option>)}
            </Sel>
          </Field>
          <Field label="Status" half>
            <Sel value={f.status} onChange={(e) => up("status", e.target.value)}>
              {["Ativo","Pausado","Encerrado"].map((s) => <option key={s}>{s}</option>)}
            </Sel>
          </Field>
        </FormRow>
        <Field label="Especialidade / Nicho">
          <Inp value={f.specialty || ""} onChange={(e) => up("specialty", e.target.value)} placeholder="Ortopedia, E-commerce de roupas..." />
        </Field>
        <Field label="Plataformas em que o cliente anuncia">
          <PlataformasPicker value={f.notes || ""} onChange={(v) => up("notes", v)} />
        </Field>
        {/* Ao salvar, o AI Manager cria a planilha, o cliente de Meta Ads e o do painel (api/ai-manager.js). */}
        <Field label="Conta de anúncios Meta">
          <Inp value={f.meta_ad_account_id || ""} onChange={(e) => up("meta_ad_account_id", e.target.value.replace(/^act_/i, "").replace(/\D/g, ""))} placeholder="Só os números. Ex: 851522139463791" inputMode="numeric" />
        </Field>
        <Field label="Links (site, Instagram, etc.)">
          <Inp value={f.links || ""} onChange={(e) => up("links", e.target.value)} />
        </Field>
        <Field label="Etapa atual do ciclo">
          <Sel value={f.cycle_stage} onChange={(e) => up("cycle_stage", e.target.value)}>
            {CYCLE.map((s) => <option key={s.id} value={s.id}>{s.s}. {s.l}</option>)}
          </Sel>
        </Field>

        <div style={{ borderTop: "1px solid var(--line)", margin: "4px 0 4px", paddingTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10 }}>Anamnese / Briefing</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <FormRow>
              <Field label="Orçamento Total (R$)" half>
                <Inp type="number" min="0" value={f.orcamento_total || ""} onChange={(e) => up("orcamento_total", e.target.value)} placeholder="Ex: 3000" />
              </Field>
              <Field label="Dia de Envio do Relatório" half>
                <Inp type="number" min="1" max="31" value={f.dia_relatorio || ""} onChange={(e) => up("dia_relatorio", e.target.value)} placeholder="Ex: 5" />
              </Field>
            </FormRow>
            <FormRow>
              <Field label="Verba Meta Ads (R$)" half>
                <Inp type="number" min="0" value={f.orcamento_meta || ""} onChange={(e) => up("orcamento_meta", e.target.value)} placeholder="Ex: 2000" />
              </Field>
              <Field label="Verba Google Ads (R$)" half>
                <Inp type="number" min="0" value={f.orcamento_google || ""} onChange={(e) => up("orcamento_google", e.target.value)} placeholder="Ex: 1000" />
              </Field>
            </FormRow>
            <Field label="Cidades (uma por linha ou separadas por vírgula)">
              <Txta rows={2} value={f.cidades || ""} onChange={(e) => up("cidades", e.target.value)} placeholder="Ex: Salvador&#10;Lauro de Freitas&#10;Simões Filho" />
            </Field>
            <FormRow>
              <Field label="Site / Landing Page" half>
                <Inp value={f.site || ""} onChange={(e) => up("site", e.target.value)} placeholder="https://..." />
              </Field>
              <Field label="WhatsApp do Cliente" half>
                <Inp value={f.whatsapp || ""} onChange={(e) => up("whatsapp", e.target.value)} placeholder="71 9 9999-9999" />
              </Field>
            </FormRow>
            <FormRow>
              <Field label="WhatsApp da Secretária" half>
                <Inp value={f.whatsapp_secretaria || ""} onChange={(e) => up("whatsapp_secretaria", e.target.value)} placeholder="71 9 9999-9999" />
              </Field>
              <Field label="Nome da Secretária" half>
                <Inp value={f.nome_secretaria || ""} onChange={(e) => up("nome_secretaria", e.target.value)} placeholder="Ex: Carol, Ana..." />
              </Field>
            </FormRow>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "2px 0" }}>
              <input
                type="checkbox"
                id="cliente_envia_criativos"
                checked={!!f.cliente_envia_criativos}
                onChange={(e) => up("cliente_envia_criativos", e.target.checked)}
                style={{ width: 16, height: 16, cursor: "pointer", accentColor: "var(--primary)" }}
              />
              <label htmlFor="cliente_envia_criativos" style={{ fontSize: 13, color: "var(--ink)", cursor: "pointer" }}>
                Cliente enviará os criativos
              </label>
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".06em", marginTop: 4 }}>Persona</div>
            <Field label="Público-alvo (quem é)">
              <Txta rows={2} value={f.publico_alvo || ""} onChange={(e) => up("publico_alvo", e.target.value)} placeholder="Ex: Homens e mulheres, 35-60 anos, região de Salvador..." />
            </Field>
            <Field label="Dores principais">
              <Txta rows={2} value={f.dores || ""} onChange={(e) => up("dores", e.target.value)} placeholder="Ex: Dor crônica no joelho, limitação de movimento..." />
            </Field>
            <Field label="Objeções comuns">
              <Txta rows={2} value={f.objecoes || ""} onChange={(e) => up("objecoes", e.target.value)} placeholder="Ex: É caro, vou esperar mais um pouco, tenho medo..." />
            </Field>
            <Field label="Solução oferecida">
              <Txta rows={2} value={f.solucao || ""} onChange={(e) => up("solucao", e.target.value)} placeholder="Ex: Medicina Regenerativa — tratamento sem cirurgia..." />
            </Field>
            <Field label="Transformação / Resultado prometido">
              <Txta rows={2} value={f.transformacao || ""} onChange={(e) => up("transformacao", e.target.value)} placeholder="Ex: Volta a caminhar, praticar esporte, ter qualidade de vida..." />
            </Field>

            {f.type === "Médico" && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".06em", marginTop: 4 }}>Convênios · um por linha</div>
                <Field label="Convênios que atende">
                  <Txta rows={3} value={f.convenios_atende || ""} onChange={(e) => up("convenios_atende", e.target.value)} placeholder={"Ex: Bradesco Saúde\nCassi\nUnimed"} />
                </Field>
                <Field label="Convênios que NÃO atende (nunca entram em anúncio)">
                  <Txta rows={3} value={f.convenios_nao_atende || ""} onChange={(e) => up("convenios_nao_atende", e.target.value)} placeholder={"Ex: SulAmérica\nHapvida\nParticular"} />
                </Field>
              </>
            )}

            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".06em", marginTop: 4 }}>Estratégia</div>
            <Field label="Estratégias já utilizadas">
              <Txta rows={3} value={f.estrategias_utilizadas || ""} onChange={(e) => up("estrategias_utilizadas", e.target.value)} placeholder="Ex: Lead form com oferta de consulta gratuita — pausamos pois CPL subiu. Carrossel de depoimentos funcionou bem no AC de mar/26..." />
            </Field>

            {/* ── Funil de Marketing ── */}
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".06em", marginTop: 8 }}>Funil de Marketing</div>
            {[
              { key: "funil_topo",  label: "🔝 Topo de Funil — Consciência",  sub: "Audiência fria — parar o scroll, gerar identificação" },
              { key: "funil_meio",  label: "🔄 Meio de Funil — Consideração", sub: "Audiência morna — construir autoridade, eliminar concorrência" },
              { key: "funil_fundo", label: "🎯 Fundo de Funil — Decisão",     sub: "Audiência quente — converter quem já te conhece" },
            ].map(({ key, label, sub }) => {
              const val = f[key] || {};
              return (
                <div key={key} style={{ border: "1px solid var(--line-soft)", borderRadius: 8, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)" }}>{label}</div>
                    <div style={{ fontSize: 11, color: "var(--faint)" }}>{sub}</div>
                  </div>
                  <Field label="Objetivo">
                    <Inp value={val.objetivo || ""} onChange={(e) => upFunil(key, "objetivo", e.target.value)} placeholder="Ex: Parar o scroll, gerar identificação com o problema" />
                  </Field>
                  <Field label="Público nesta etapa">
                    <Txta rows={2} value={val.publico || ""} onChange={(e) => upFunil(key, "publico", e.target.value)} placeholder="Ex: Homens 35-55 interessados em ortopedia / frio sem contato anterior" />
                  </Field>
                  <Field label="Criativos planejados">
                    <Txta rows={4} value={val.criativos || ""} onChange={(e) => upFunil(key, "criativos", e.target.value)} placeholder={"→ Vídeo 01 — Você falando + texto animado\n→ Estático 01 — Imagem com headline de dor\n→ Carrossel 01 — Educação da jornada"} />
                  </Field>
                </div>
              );
            })}
          </div>
        </div>

        <Btn variant="accent" full onClick={save} disabled={saving || !f.name.trim()}>
          {saving ? "Salvando..." : "Salvar cliente"}
        </Btn>
      </div>
    </Modal>
  );
}

export default function App() {
  const [auth, setAuth]         = useState(() => localStorage.getItem("rc2_auth") === "1");
  const [view, setView]         = useState("rotina-do-dia");
  const [clients, setClients]   = useState([]);
  const [tasks, setTasks]       = useState([]);
  const [events, setEvents]     = useState([]);
  const [criativos, setCriativos]     = useState([]);
  const [concorrentes, setConcorrentes] = useState([]);
  const [analises, setAnalises]       = useState([]);
  const [cconcorrentes, setCConcorrentes] = useState([]);
  const [analisesCliente, setAnalisesCliente] = useState([]);
  const [roteiros, setRoteiros] = useState([]);
  const [hooks, setHooks]       = useState([]);
  const [prompts, setPrompts]   = useState([]);
  const [historico, setHistorico] = useState([]);
  const [planos, setPlanos]     = useState([]);
  const [metaAds, setMetaAds]   = useState([]);
  const [kpis, setKpis]         = useState([]);
  const [otimizacoes, setOtimizacoes] = useState([]);
  const [anotacoes, setAnotacoes]     = useState([]);
  const [socialPosts, setSocialPosts] = useState([]);
  const [automacoes, setAutomacoes]   = useState([]);
  const [agenda, setAgenda]           = useState([]);
  // A leitura da agenda deu certo? So com `true` a auto-geracao pode rodar:
  // gerar rodizio a partir de uma leitura que falhou duplica o mes inteiro.
  const [agendaCarregada, setAgendaCarregada] = useState(false);
  const [loading, setLoading]   = useState(true);
  const [newClientModal, setNewClientModal]   = useState(false);
  const [editClientModal, setEditClientModal] = useState(null);
  const [newCriativoModal, setNewCriativoModal]     = useState(false);
  const [newCriativoClientId, setNewCriativoClientId] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const [cl, tk, ev, cr, co, an, cc, hk, pr, hi, ro, ac, pl, ma, kp, ot, ano, sp, aut, ag] = await Promise.all([
          db("clients",                    "GET", null, "?order=name"),
          db("rc2_tasks",                  "GET", null, "?archived=eq.false&order=created_at.desc"),
          db("rc2_events",                 "GET", null, "?order=event_date,event_time"),
          db("rc2_criativos",              "GET", null, "?order=created_at.desc"),
          db("rc2_concorrentes",           "GET", null, "?order=nome"),
          db("rc2_analise_estrategica",    "GET", null, "?order=created_at.desc"),
          db("rc2_criativos_concorrentes", "GET", null, "?order=created_at.desc"),
          db("rc2_hooks",                  "GET", null, "?order=created_at.desc"),
          db("prompts",                    "GET", null, "?order=category,title"),
          db("rc2_historico_cliente",      "GET", null, "?order=mes.desc"),
          db("rc2_roteiros",               "GET", null, "?order=created_at.desc"),
          db("rc2_analises_cliente",       "GET", null, "?order=created_at.desc"),
          db("rc2_planos",                 "GET", null, "?order=created_at.desc"),
          db("rc2_meta_ads",               "GET", null, "?order=created_at.desc"),
          db("rc2_kpis",                   "GET", null, "?order=synced_at.desc").catch(() => []),
          db("rc2_otimizacoes",            "GET", null, "?order=data.desc").catch(() => []),
          db("rc2_anotacoes",              "GET", null, "?order=data.desc").catch(() => []),
          db("rc2_social_posts",           "GET", null, "?order=data_prevista").catch(() => []),
          db("rc2_automacoes",             "GET", null, "?order=ordem").catch(() => []),
          // NAO usar `.catch(() => [])` aqui. A agenda tem auto-geracao, e
          // devolver [] numa falha de leitura faz "nao consegui ler" virar
          // "nao existe" — foi assim que em 05/09/2026 um mes inteiro de
          // rodizio nasceu por cima do que ja existia, sem ninguem clicar.
          // `null` preserva a diferenca; ver `agendaCarregada` abaixo.
          db("rc2_agenda_dias",            "GET", null, "?order=date").catch(() => null),
        ]);
        setClients(cl || []);
        setTasks(tk || []);
        setEvents(ev || []);
        setCriativos(cr || []);
        setConcorrentes(co || []);
        setAnalises(an || []);
        setCConcorrentes(cc || []);
        setHooks(hk || []);
        setPrompts(pr || []);
        setHistorico(hi || []);
        setRoteiros(ro || []);
        setAnalisesCliente(ac || []);
        setPlanos(pl || []);
        setMetaAds(ma || []);
        setKpis(kp || []);
        setOtimizacoes(ot || []);
        setAnotacoes(ano || []);
        setSocialPosts(sp || []);
        setAutomacoes(aut || []);
        setAgenda(ag || []);
        setAgendaCarregada(ag !== null);
      } catch (e) {
        console.error("Erro ao carregar dados:", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function updateClient(id, data) {
    await db("clients", "PATCH", data, `?id=eq.${id}`);
    setClients((prev) => prev.map((c) => c.id === id ? { ...c, ...data } : c));
  }
  /**
   * Espelha as plataformas declaradas no RC2 AI Manager.
   *
   * O CRM é o dono do campo; o AI Manager é quem o usa para decidir o que
   * cobrar em `missing_sources`. O sentido do sync é um só — CRM → AI
   * Manager — porque dois donos do mesmo campo divergem em silêncio.
   *
   * SEMPRE DEPOIS DE SALVAR, E NUNCA BLOQUEANDO. O cadastro já está no
   * Supabase quando isto roda. Se o motor estiver fora, o cliente
   * continua salvo e o aviso é console — a mesma regra que fez os
   * workflows do n8n pararem de perder o log quando o WhatsApp caía.
   */
  async function espelharPlataformas(cliente) {
    try {
      const { values } = parsePlataformas(cliente?.notes || "");
      const r = await fetch("/api/ai-manager", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientName: cliente?.name,
          platforms: values,
          clientId: cliente?.id,
          metaAccountId: cliente?.meta_ad_account_id || "",
        }),
      });
      const out = await r.json().catch(() => null);
      if (!out?.mirrored) {
        console.info("Plataformas não espelhadas no AI Manager:", out?.reason, out?.detail || "");
        // Conta Meta recusada (número errado, sem acesso na BM): o cadastro já
        // está salvo, mas quem digitou precisa saber que o Dash não vai mostrar.
        if (cliente?.meta_ad_account_id && out?.reason !== "not_configured") {
          alert("Cliente salvo, mas o AI Manager não cadastrou a conta Meta: " + (out?.detail || out?.reason || "erro"));
        }
      }
    } catch (e) {
      console.info("Plataformas não espelhadas no AI Manager:", String(e.message || e));
    }
  }

  async function saveClient(data) {
    const clean = {
      ...data,
      orcamento_total:  data.orcamento_total  !== "" && data.orcamento_total  != null ? Number(data.orcamento_total)  : null,
      orcamento_meta:   data.orcamento_meta   !== "" && data.orcamento_meta   != null ? Number(data.orcamento_meta)   : null,
      orcamento_google: data.orcamento_google !== "" && data.orcamento_google != null ? Number(data.orcamento_google) : null,
      dia_relatorio:    data.dia_relatorio    !== "" && data.dia_relatorio    != null ? Number(data.dia_relatorio)    : null,
      // Só vai no corpo quando o campo existe no formulário (nunca força a coluna).
      ...("grupo" in data ? { grupo: String(data.grupo || "").trim() || null } : {}),
    };
    if (clean.id) {
      await updateClient(clean.id, clean);
      espelharPlataformas(clean);
    } else {
      const [created] = await db("clients", "POST", clean);
      setClients((prev) => [...prev, created]);
      espelharPlataformas(created || clean);
    }
  }

  async function newTask(data) {
    const [created] = await db("rc2_tasks", "POST", data);
    setTasks((prev) => [created, ...prev]);
    return created;
  }
  async function updateTask(id, data) {
    const result = await db("rc2_tasks", "PATCH", data, `?id=eq.${id}`);
    setTasks((prev) => prev.map((t) => t.id === id ? { ...t, ...data } : t));
    return result;
  }
  async function deleteTask(id) {
    await db("rc2_tasks", "DELETE", null, `?id=eq.${id}`);
    setTasks((prev) => prev.filter((t) => t.id !== id));
  }

  async function newHistorico(data) {
    const [created] = await db("rc2_historico_cliente", "POST", data);
    setHistorico((prev) => [created, ...prev]);
  }

  async function newCriativo(data) {
    const [created] = await db("rc2_criativos", "POST", data);
    setCriativos((prev) => [created, ...prev]);
    return created;
  }
  async function updateCriativo(id, data) {
    await db("rc2_criativos", "PATCH", data, `?id=eq.${id}`);
    setCriativos((prev) => prev.map((cr) => cr.id === id ? { ...cr, ...data } : cr));
  }

  async function newConcorrente(data) {
    const [created] = await db("rc2_concorrentes", "POST", data);
    setConcorrentes((prev) => [...prev, created]);
  }
  async function updateConcorrente(id, data) {
    await db("rc2_concorrentes", "PATCH", data, `?id=eq.${id}`);
    setConcorrentes((prev) => prev.map((c) => c.id === id ? { ...c, ...data } : c));
  }

  async function newAnalise(data) {
    const [created] = await db("rc2_analise_estrategica", "POST", data);
    setAnalises((prev) => [created, ...prev]);
  }
  async function newCriativoConcorrente(data) {
    const [created] = await db("rc2_criativos_concorrentes", "POST", data);
    setCConcorrentes((prev) => [created, ...prev]);
  }

  async function newAnaliseCliente(data) {
    const [created] = await db("rc2_analises_cliente", "POST", data);
    setAnalisesCliente((prev) => [created, ...prev]);
  }
  async function updateAnaliseCliente(id, data) {
    await db("rc2_analises_cliente", "PATCH", data, `?id=eq.${id}`);
    setAnalisesCliente((prev) => prev.map((a) => a.id === id ? { ...a, ...data } : a));
  }
  async function deleteAnaliseCliente(id) {
    await db("rc2_analises_cliente", "DELETE", null, `?id=eq.${id}`);
    setAnalisesCliente((prev) => prev.filter((a) => a.id !== id));
  }

  async function newPlano(data) {
    const [created] = await db("rc2_planos", "POST", data);
    setPlanos((prev) => [created, ...prev]);
  }
  async function updatePlano(id, data) {
    await db("rc2_planos", "PATCH", data, `?id=eq.${id}`);
    setPlanos((prev) => prev.map((p) => p.id === id ? { ...p, ...data } : p));
  }
  async function deletePlano(id) {
    await db("rc2_planos", "DELETE", null, `?id=eq.${id}`);
    setPlanos((prev) => prev.filter((p) => p.id !== id));
  }

  async function newMetaAds(data) {
    const [created] = await db("rc2_meta_ads", "POST", data);
    setMetaAds((prev) => [created, ...prev]);
  }
  async function updateMetaAds(id, data) {
    await db("rc2_meta_ads", "PATCH", data, `?id=eq.${id}`);
    setMetaAds((prev) => prev.map((m) => m.id === id ? { ...m, ...data } : m));
  }
  async function deleteMetaAds(id) {
    await db("rc2_meta_ads", "DELETE", null, `?id=eq.${id}`);
    setMetaAds((prev) => prev.filter((m) => m.id !== id));
  }

  async function newOtimizacao(data) {
    const [created] = await db("rc2_otimizacoes", "POST", data);
    setOtimizacoes((prev) => [created, ...prev]);
  }
  async function updateOtimizacao(id, data) {
    await db("rc2_otimizacoes", "PATCH", data, `?id=eq.${id}`);
    setOtimizacoes((prev) => prev.map((o) => o.id === id ? { ...o, ...data } : o));
  }
  async function deleteOtimizacao(id) {
    await db("rc2_otimizacoes", "DELETE", null, `?id=eq.${id}`);
    setOtimizacoes((prev) => prev.filter((o) => o.id !== id));
  }

  async function newSocialPost(data) {
    const [created] = await db("rc2_social_posts", "POST", data);
    setSocialPosts((prev) => [...prev, created].sort((a, b) => a.data_prevista.localeCompare(b.data_prevista)));
    return created;
  }
  async function updateSocialPost(id, data) {
    await db("rc2_social_posts", "PATCH", data, `?id=eq.${id}`);
    setSocialPosts((prev) => prev.map((p) => p.id === id ? { ...p, ...data } : p));
  }
  async function deleteSocialPost(id) {
    await db("rc2_social_posts", "DELETE", null, `?id=eq.${id}`);
    setSocialPosts((prev) => prev.filter((p) => p.id !== id));
  }

  async function newAnotacao(data) {
    const [created] = await db("rc2_anotacoes", "POST", data);
    setAnotacoes((prev) => [created, ...prev]);
  }
  async function deleteAnotacao(id) {
    await db("rc2_anotacoes", "DELETE", null, `?id=eq.${id}`);
    setAnotacoes((prev) => prev.filter((a) => a.id !== id));
  }

  async function newAutomacao(data) {
    const [created] = await db("rc2_automacoes", "POST", data);
    setAutomacoes((prev) => [...prev, created]);
  }
  async function updateAutomacao(id, data) {
    await db("rc2_automacoes", "PATCH", data, `?id=eq.${id}`);
    setAutomacoes((prev) => prev.map((a) => a.id === id ? { ...a, ...data } : a));
  }
  async function deleteAutomacao(id) {
    await db("rc2_automacoes", "DELETE", null, `?id=eq.${id}`);
    setAutomacoes((prev) => prev.filter((a) => a.id !== id));
  }

  async function newRoteiro(data) {
    const [created] = await db("rc2_roteiros", "POST", data);
    setRoteiros((prev) => [created, ...prev]);
  }
  async function updateRoteiro(id, data) {
    await db("rc2_roteiros", "PATCH", data, `?id=eq.${id}`);
    setRoteiros((prev) => prev.map((r) => r.id === id ? { ...r, ...data } : r));
  }
  async function deleteRoteiro(id) {
    await db("rc2_roteiros", "DELETE", null, `?id=eq.${id}`);
    setRoteiros((prev) => prev.filter((r) => r.id !== id));
  }

  async function newHook(data) {
    const [created] = await db("rc2_hooks", "POST", data);
    setHooks((prev) => [created, ...prev]);
  }
  async function updateHook(id, data) {
    await db("rc2_hooks", "PATCH", data, `?id=eq.${id}`);
    setHooks((prev) => prev.map((h) => h.id === id ? { ...h, ...data } : h));
  }

  async function newPrompt(data) {
    const [created] = await db("prompts", "POST", data);
    setPrompts((prev) => [created, ...prev]);
  }
  async function updatePrompt(id, data) {
    await db("prompts", "PATCH", data, `?id=eq.${id}`);
    setPrompts((prev) => prev.map((p) => p.id === id ? { ...p, ...data } : p));
  }

  async function newAgendaDia(data) {
    const [created] = await db("rc2_agenda_dias", "POST", data);
    setAgenda((prev) => [...prev, created].sort((a, b) => a.date.localeCompare(b.date)));
    return created;
  }
  async function newAgendaDiasBatch(entries) {
    const created = await db("rc2_agenda_dias", "POST", entries);
    setAgenda((prev) => [...prev, ...(created || [])].sort((a, b) => a.date.localeCompare(b.date)));
  }
  async function updateAgendaDia(id, data) {
    await db("rc2_agenda_dias", "PATCH", data, `?id=eq.${id}`);
    setAgenda((prev) => prev.map((d) => d.id === id ? { ...d, ...data } : d));
  }

  async function newEvent(data) {
    const [created] = await db("rc2_events", "POST", data);
    setEvents((prev) => [...prev, created].sort((a, b) => a.event_date.localeCompare(b.event_date)));
  }
  async function updateEvent(id, data) {
    await db("rc2_events", "PATCH", data, `?id=eq.${id}`);
    setEvents((prev) => prev.map((e) => e.id === id ? { ...e, ...data } : e));
  }
  async function deleteEvent(id) {
    await db("rc2_events", "DELETE", null, `?id=eq.${id}`);
    setEvents((prev) => prev.filter((e) => e.id !== id));
  }

  function goClient(id) { setView(`client:${id}`); }
  const clientId     = view.startsWith("client:") ? view.split(":")[1] : null;
  const activeClient = clientId ? clients.find((c) => c.id === clientId) : null;

  if (!auth) return <LoginView onAuth={() => setAuth(true)} />;

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "var(--bg)", flexDirection: "column", gap: 16 }}>
        <Marca height={30} />
        <Spinner size={28} />
        <div style={{ fontSize: 13, color: "var(--muted)" }}>Carregando dados...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div className="shell" style={{ height: "100vh" }}>
        <Sidebar view={view} setView={setView} clients={clients} onLogout={() => { localStorage.removeItem("rc2_auth"); setAuth(false); }} />

        <div className="main" style={{ overflowY: "auto" }}>
          {view === "rotina-do-dia" && (
            <RotinaDoDiaView clients={clients} tasks={tasks} events={events} planos={planos} historico={historico} anotacoes={anotacoes} onGoClient={goClient} />
          )}
          {view === "dashboard" && (
            <DashboardView clients={clients} tasks={tasks} events={events} kpis={kpis} planos={planos} onGoClient={goClient} />
          )}
          {view === "performance" && (
            <PerformanceView kpis={kpis} clients={clients} />
          )}
          {view === "clients" && (
            <ClientsView clients={clients} tasks={tasks} onGoClient={goClient} onNewClient={() => setNewClientModal(true)} />
          )}
          {activeClient && (
            <ClientDetail
              client={activeClient}
              tasks={tasks.filter((t) => t.client_id === activeClient.id)}
              historico={historico.filter((h) => h.client_id === activeClient.id)}
              criativos={criativos.filter((cr) => cr.client_id === activeClient.id)}
              concorrentes={concorrentes}
              analisesCliente={analisesCliente.filter((a) => a.client_id === activeClient.id)}
              planos={planos.filter((p) => p.client_id === activeClient.id)}
              metaAds={metaAds.filter((m) => m.client_id === activeClient.id)}
              otimizacoes={otimizacoes.filter((o) => o.client_id === activeClient.id)}
              anotacoes={anotacoes.filter((a) => a.client_id === activeClient.id)}
              socialPosts={socialPosts.filter((p) => p.client_id === activeClient.id)}
              onUpdateClient={updateClient}
              onUpdateTask={updateTask}
              onNewTask={newTask}
              onNewHistorico={newHistorico}
              onDeleteTask={deleteTask}
              onNewCriativo={() => { setNewCriativoClientId(activeClient.id); setNewCriativoModal(true); }}
              onSaveCriativo={newCriativo}
              onUpdateCriativo={updateCriativo}
              onEditClient={() => setEditClientModal(activeClient)}
              onNewAnalise={newAnaliseCliente}
              onUpdateAnalise={updateAnaliseCliente}
              onDeleteAnalise={deleteAnaliseCliente}
              onNewPlano={newPlano}
              onUpdatePlano={updatePlano}
              onDeletePlano={deletePlano}
              onNewMetaAds={newMetaAds}
              onUpdateMetaAds={updateMetaAds}
              onDeleteMetaAds={deleteMetaAds}
              onNewOtimizacao={newOtimizacao}
              onUpdateOtimizacao={updateOtimizacao}
              onDeleteOtimizacao={deleteOtimizacao}
              onNewAnotacao={newAnotacao}
              onDeleteAnotacao={deleteAnotacao}
              onNewSocialPost={newSocialPost}
              onUpdateSocialPost={updateSocialPost}
              onDeleteSocialPost={deleteSocialPost}
            />
          )}
          {view === "criativos" && (
            <CriativosView criativos={criativos} clients={clients} onNew={() => { setNewCriativoClientId(null); setNewCriativoModal(true); }} onUpdate={updateCriativo} />
          )}
          {view === "concorrentes" && (
            <ConcorrentesView clients={clients} concorrentes={concorrentes} analises={analises} criativosConcorrentes={cconcorrentes} onNew={newConcorrente} onUpdate={updateConcorrente} onNewAnalise={newAnalise} onNewCriativoConcorrente={newCriativoConcorrente} />
          )}
          {view === "roteiros" && (
            <RoteirosView roteiros={roteiros} clients={clients} onNew={newRoteiro} onUpdate={updateRoteiro} onDelete={deleteRoteiro} />
          )}
          {view === "hooks" && (
            <HooksView hooks={hooks} onNew={newHook} onUpdate={updateHook} />
          )}
          {view === "prompts" && (
            <PromptsView prompts={prompts} onNew={newPrompt} onUpdate={updatePrompt} />
          )}
          {view === "agenda" && (
            <AgendaView
              events={events} clients={clients}
              tasks={tasks} planos={planos} historico={historico} anotacoes={anotacoes}
              agenda={agenda} agendaCarregada={agendaCarregada}
              onNew={newEvent} onUpdate={updateEvent} onDelete={deleteEvent}
              onNewAgenda={newAgendaDia}
              onUpdateAgenda={updateAgendaDia}
              onNewAgendaBatch={newAgendaDiasBatch}
              onGoClient={goClient}
              onUpdateTask={updateTask}
              onUpdatePlano={updatePlano}
            />
          )}
          {view === "claude-design" && (
            <ClaudeDesignView />
          )}
          {view === "prospect" && (
            <ProspectView />
          )}
          {view === "social-media" && (
            <SocialMediaView />
          )}
          {view === "biblioteca" && (
            <BibliotecaView />
          )}
          {view === "skills" && (
            <SkillsView />
          )}
          {view === "crm-medico" && (
            <CrmMedicoView />
          )}
          {view === "automacoes" && (
            <AutomacoesView
              automacoes={automacoes}
              onUpdate={updateAutomacao}
              onNew={newAutomacao}
              onDelete={deleteAutomacao}
            />
          )}
          {view === "fluxomed" && (
            <FluxoMedView />
          )}
        </div>

        {newClientModal && (
          <ClientFormModal onClose={() => setNewClientModal(false)} onSave={async (data) => { await saveClient(data); setNewClientModal(false); }} />
        )}
        {editClientModal && (
          <ClientFormModal initial={editClientModal} onClose={() => setEditClientModal(null)} onSave={async (data) => { await saveClient(data); setEditClientModal(null); }} />
        )}
        {newCriativoModal && (
          <CriativoFormModal
            clients={clients}
            clientId={newCriativoClientId}
            onClose={() => { setNewCriativoModal(false); setNewCriativoClientId(null); }}
            onSave={async (data) => { await newCriativo(data); setNewCriativoModal(false); setNewCriativoClientId(null); }}
          />
        )}
      </div>
    </>
  );
}
